import { App as CapacitorApp } from '@capacitor/app'
import { redactBayawatchEvent, redactUrl } from '@/bayawatchPrivacy'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import {
  filterBayaWatchPayload,
  setHttpFailureReporter,
} from '@/lib/monitoring/httpFailureReporting'
import { isReplaySafePage, setReplayStarted } from '@/lib/monitoring/sessionReplayGate'

// BayaWatch — self-hosted uptime / RUM / session-replay (watch.bayalab.com).
// This is the app's monitoring stack (it replaced Sentry). Consumed as the
// `@bayawatch/sdk` npm module and initialized once on startup from `main.tsx`.

const BAYAWATCH_DSN = 'cmotzwslv64r3bx77h6k6k46p'
const BAYAWATCH_ENDPOINT = 'https://watch.bayalab.com'

// Which deployment(s) BayaWatch runs on. We gate by HOST rather than by
// env-string because VITE_SENTRY_ENVIRONMENT / VITE_APP_ENV are unset in the
// deployed builds, so `env.environment` collapses to Vite's MODE and cannot
// distinguish staging from production. Hostname is reliable on both web (the
// public domain) and native (the VITE_API_URL baked into the build).
//
// PROD-ONLY: record on production + whitelabel hosts under buildlog.app — prod
// (use.buildlog.app), demo (demo.buildlog.app), and each current/future
// whitelabel client (e.g. prologis.buildlog.app) — but EXCLUDE staging, so its
// traffic no longer pollutes the shared dashboard now that prod recording is
// confirmed. (A buildlog.app suffix match still covers new client subdomains
// with no code change; staging is the one explicit carve-out.) Localhost /
// dev-server / bare-IP hosts never match; use VITE_BAYAWATCH_FORCE for local
// testing. If a whitelabel ever gets a fully custom domain, add a second suffix.
const STAGING_HOST = 'staging.use.buildlog.app'
const isEnabledHost = (host: string): boolean =>
  host !== STAGING_HOST && (host === 'buildlog.app' || host.endsWith('.buildlog.app'))

// Local override for testing / stress-testing: set VITE_BAYAWATCH_FORCE=true
// (in app/.env.local for web, or the native build env) to load BayaWatch
// regardless of host AND regardless of build provenance — e.g. on localhost,
// an iOS simulator, or an Android emulator. Never set this in a deployed build.
const FORCE_ENABLE = import.meta.env.VITE_BAYAWATCH_FORCE === 'true'

// Build provenance — true only in official deployment/release builds.
// `__DEPLOYED__` is defined in vite.config.ts from the VITE_DEPLOYED env var,
// which is set by the web Dockerfile build stage and the native
// `*:build:release` scripts, never by a plain local `npm run build`.
//
// This closes the one gap the host check alone can't cover: on NATIVE the
// "deployment host" is merely the hostname of the baked-in VITE_API_URL, so a
// developer's LOCAL native build pointed at the staging/prod API (the
// documented CLAUDE.md iOS setup) would otherwise match `isEnabledHost` and
// record real sessions from a dev machine. Requiring a deployment build too
// means local builds never record — use VITE_BAYAWATCH_FORCE to opt in.
const IS_DEPLOYED_BUILD = __DEPLOYED__

/** Guard against double-init (e.g. HMR re-evaluating this module in dev). */
let initialized = false

/**
 * The host identifying the current deployment, cross-platform.
 * - Web: the page's own hostname.
 * - Native: `capacitor://localhost` has no meaningful host, so we use the
 *   hostname of the API URL the build is pointed at (VITE_API_URL).
 */
function getDeploymentHost(): string {
  if (isNativePlatform()) {
    const apiUrl = import.meta.env.VITE_API_URL
    if (!apiUrl) return ''
    try {
      return new URL(apiUrl).hostname
    } catch {
      return ''
    }
  }
  return typeof window !== 'undefined' ? window.location.hostname : ''
}

/**
 * The URL of the link that cold-launched the native app, if any. On a cold
 * start from a universal link the WebView boots on `/` and `main.tsx` only
 * navigates to the link's path once the router is ready — by then the replay
 * gate below has long been decided on `/`. So a secret launch link has to be
 * known BEFORE `init()`.
 */
async function nativeLaunchUrl(): Promise<string | null> {
  try {
    const launch = await CapacitorApp.getLaunchUrl()
    return launch?.url ?? null
  } catch {
    return null
  }
}

function initBayaWatch(): void {
  if (initialized || typeof window === 'undefined') return

  const host = getDeploymentHost()
  // Record only on real deployments to a buildlog.app host. FORCE_ENABLE
  // overrides both checks for local testing.
  const enabled = FORCE_ENABLE || (IS_DEPLOYED_BUILD && isEnabledHost(host))
  if (!enabled) {
    if (import.meta.env.DEV) {
      const reason = !IS_DEPLOYED_BUILD
        ? 'not a deployment build (VITE_DEPLOYED unset)'
        : `host "${host}" is not a buildlog.app host`
      console.log(`skipping bayawatch init - ${reason}`)
    }
    return
  }
  initialized = true

  // Lazy-load the SDK so it's only fetched/parsed on enabled hosts (the gate
  // above), keeping the monitoring bundle out of the critical path everywhere
  // and fully absent on non-deployed hosts (local dev, e2e).
  void import('@bayawatch/sdk')
    .then(async ({ init }) => {
      const native = isNativePlatform()
      const launchUrl = native ? await nativeLaunchUrl() : null
      // Since SDK 0.4.0 every page URL the SDK sends — error events, session
      // init and replay batches, rrweb's Meta href and the URL-bearing DOM
      // attributes in snapshots and mutations — goes through `sanitizeUrl`
      // below, so the token in `/public/reports/:token` and its siblings no
      // longer leaves through a URL.
      //
      // Replay nevertheless stays OFF for page loads whose path carries a
      // secret (`sessionReplayGate.ts`), as defence in depth:
      // - `sanitizeUrl` only sees URLs. Text nodes, input values and
      //   attributes the SDK does not classify as URL-bearing are recorded as
      //   rendered, and `/public/issues/:token` mounts the whole
      //   `ProjectSingleIssue` subtree, which nobody has audited for a token
      //   rendered as text.
      // - The gate is deny-by-default over ANY opaque path segment, while
      //   `redactUrl` knows exactly the four token routes. A future route that
      //   puts a secret in the path stays unrecorded without anyone
      //   remembering to extend the redactor.
      // The cost is replays of anonymous visitors on shared links only.
      // Error capture stays on there.
      //
      // A native cold start from a link boots on `/`, so the launch URL is
      // gated too. A WARM deep link into a token route tears the SDK down
      // before navigating (`lib/deepLinkNavigation.ts`); since 0.4.0
      // `destroy()` also cancels a recording start still queued behind the
      // `/sdk-config` preflight, so that teardown holds even in the first
      // request of a launch.
      const replay =
        isReplaySafePage(window.location.href) &&
        (launchUrl === null || isReplaySafePage(launchUrl))
      setReplayStarted(replay)
      // Full session replay on every platform (web + iOS/Android native).
      //
      // `inlineImages: false` EVERYWHERE, web included. Measured 2026-09-01 on
      // production: rrweb's image inlining sizes a canvas to the image's
      // intrinsic `naturalWidth x naturalHeight` with NO cap and then calls
      // `toDataURL(dataURLOptions.type, dataURLOptions.quality)` — and the SDK
      // passes no `dataURLOptions`, so both are `undefined`, which means a
      // LOSSLESS PNG, encoded synchronously on the main thread
      // (rrweb-snapshot 2.0.1, `serializeElementNode`). The lightbox renders the
      // `-large` tier, capped at 2560px on the long edge
      // (api `FileUploadEventSubscriber::LARGE_MAX_EDGE`), so opening a photo
      // paid a full-resolution PNG encode plus base64 per image. Symptoms: the
      // gallery froze — close button and nav arrows stopped responding — and the
      // ingest POST came back `413`, which the transport does not retry (4xx is
      // terminal), so the whole batch was dropped. We were paying the freeze AND
      // losing the replay events for exactly the window we wanted to see.
      //
      // An image that is not yet loaded has its encode deferred to the `load`
      // event; one that is already `complete` with a non-zero `naturalWidth` is
      // encoded immediately, inline in serialization
      // (`rrweb-snapshot.js:1091-1092`). The deferred half is why the freeze
      // landed AFTER the lightbox had already painted.
      //
      // Native was already false for an unrelated reason: rrweb's asset capture
      // flips the live `<img>` to crossOrigin and the CORS re-fetch of
      // cross-origin media (no ACAO header) fails, blanking the already-rendered
      // attachment — the SDK force-disables it in WebViews anyway, but passing
      // false explicitly also skips its override warning. The SDK also uses a
      // lighter native ingest cadence (15s / 50 events) and auto-flushes on
      // `visibilitychange -> hidden`.
      //
      // Cost accepted: replays no longer embed image bytes; the `<img>` keeps its
      // `src`. That is what native replays have always looked like. The `src`
      // is the one rewritten by `sanitizeUrl`, so query VALUES become
      // `redacted` and an image whose URL is signed in the query
      // (`?signature=...`) will not load in the replay — a deliberate
      // privacy-over-fidelity trade, since a signed URL is itself a bearer
      // credential. First-party media are unsigned today: `/media/...` plus a
      // cache-bust `?v=<mtime>` (`MediaObjectUrlBuilder::withCacheBustOrNull`),
      // which the static file server ignores, so they still load. The fix that
      // brings the images back lives in the SDK, not here, and is filed against
      // the bayawatch repo. Note it needs TWO parts, not one: exposing rrweb's
      // `dataURLOptions` only reaches `toDataURL(type, quality)`
      // (`rrweb-snapshot.js:1067-1073`) — the canvas is still sized to the full
      // `naturalWidth x naturalHeight` with no cap, so bounded JPEG shrinks the
      // payload (and with it the 413) while leaving the synchronous main-thread
      // encode, i.e. the freeze, reachable. Killing the freeze additionally
      // needs a dimension bound or an off-main-thread encode.
      //
      // `collectFonts` stays tied to `native` — but NOT because it is free on
      // web: `collectFonts` installs a runtime observer that monkey-patches
      // `window.FontFace` for the whole session (`rrweb.js:12893`, wired at
      // `:13122`), so it is ongoing capture, not a one-time snapshot cost. It is
      // left as-is only because it was not part of this failure and nothing in
      // this app constructs `FontFace` at runtime today.
      const instance = init({
        dsn: BAYAWATCH_DSN,
        endpoint: BAYAWATCH_ENDPOINT,
        // The hostname IS the deployment identity, so it doubles as the
        // environment label: staging stays a clean `staging` bucket and every
        // prod deployment (incl. each whitelabel) segments under its own host
        // in the dashboard. All deployments share one DSN.
        environment: host === STAGING_HOST ? 'staging' : host,
        // App version (package.json, baked by Vite) — correlates errors and
        // replays with the release that produced them.
        release: __APP_VERSION__,
        sampleRate: replay ? 1 : 0,
        // Also blocks the SDK's late start of recording after an error, which
        // is sampled by this rate rather than by `sampleRate`.
        errorSampleRate: replay ? 1 : 0,
        // Applied by the SDK to every page URL it sends (see above). Fails
        // closed: if this throws, the SDK sends the bare origin instead.
        sanitizeUrl: href => redactUrl(href),
        captureConsoleErrors: true,
        inlineStylesheet: true,
        inlineImages: false,
        collectFonts: !native,
        // Drops automatic re-captures of axios failures the API client
        // already reported with method/path/status context, and scrubs the
        // current page's secret BY VALUE from every field (message, stack,
        // context). Then scrubs any token-route path an error text quotes
        // that is NOT the current page (e.g. a request to
        // `/api/public/reports/<t>` reported elsewhere). Filtering first keeps
        // the dedupe keys the raw axios message/stack.
        //
        // The page secrets are read from the live `location.href`, not from
        // `payload.url`: the SDK runs `sanitizeUrl` BEFORE `beforeSend`, so
        // `payload.url` already says `:token` and no longer tells which value
        // to scrub. `beforeSend` runs synchronously in the capture call, so
        // the live address is the one the payload was stamped with.
        beforeSend: payload => {
          const filtered = filterBayaWatchPayload(payload, window.location.href)
          return filtered && redactBayawatchEvent(filtered)
        },
      })
      setHttpFailureReporter(instance)
    })
    .catch(() => {
      // Monitoring is best-effort. A failed import (e.g. offline cold start) must
      // never block or crash the app.
    })
}

initBayaWatch()
